// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, Box, Icon, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { DetailsSection, EmptyState, ListSection } from '../../../../../common/components';
import OrderRefund from './OrderRefund';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  content: {
    flex: '1',
    display: 'flex',
    padding: theme.spacing(2),
  },
  wrapper: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(4),
    '&:last-child': {
      marginRight: '0',
    },
  },
  section: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  eventsSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  transactionsSection: {
    marginTop: theme.spacing(3),
  },
  transactionContent: {
    marginTop: theme.spacing(1),
  },
  transactionLabel: {
    display: 'inline-block',
    minWidth: '140px',
    fontWeight: 'bold',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2),
    '& .MuiButton-root': {
      marginLeft: theme.spacing(1),
    },
    '& .MuiButton-root:first-child': {
      marginLeft: '0',
    },
  },
  back: {
    marginRight: 'auto',
  },
}));

function OrderDetails(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const { products, order, onRefundOrder, onCancelOrder, onHideDetails, actionsDisabled, getSum } = props;

  const formatProvider = provider => {
    switch (provider) {
      case 1:
        return 'TV 2';
      case 2:
        return 'TV 2 ansat';
      case 38:
      case 39:
        return 'B2B';
      default:
        return 'Partner';
    }
  };

  const formatOrderLines = value =>
    value
      .map(orderLine => {
        const channel = products.find(ch => ch.id === orderLine.value);
        return channel?.title;
      })
      .filter(orderLine => orderLine)
      .join(', ');

  const formatEvent = value => {
    const { eventText, userEventType } = value;
    if (!userEventType) {
      return '';
    }
    const { displayText, eventSubTypeDescription, eventTypeDescription } = userEventType;
    return (
      <>
        {displayText || `${eventSubTypeDescription} - ${eventTypeDescription}`}
        <br />
        {eventText}
      </>
    );
  };

  const formatProductName = value => {
    const product = products.find(p => p.id === value);
    return product?.title;
  };

  const getOrderDetails = () => [
    {
      id: 'orderId',
      label: t('order.orderId'),
      value: order.order.orderId,
    },
    {
      id: 'orderType',
      label: t('order.orderType'),
      value: StringUtils.getEnumTranslation(t, 'order.orderTypeEnum', order.order.type),
    },
    {
      id: 'orderStatus',
      label: t('order.orderStatus'),
      value: StringUtils.getEnumTranslation(t, 'order.orderStatusEnum', order.order.status),
    },
    {
      id: 'pendingOrderStatus',
      label: t('order.orderStatusPending'),
      value: StringUtils.getEnumTranslation(
        t,
        'order.pendingOrderStatusEnum',
        order.order.pendingOrderStatus
      ),
    },
    {
      id: 'orderProvider',
      label: t('order.orderProvider'),
      value: order.order.provider,
      formatter: formatProvider,
    },
    {
      id: 'productId',
      label: t('product.productId'),
      value: order.order.product.id,
    },
    {
      id: 'productName',
      label: t('product.productName'),
      value: StringUtils.getTranslation(
        t,
        'product.types',
        order.order.product.id,
        order.order.product.title
      ),
    },
    {
      id: 'orderDuration',
      label: t('order.orderDuration'),
      value: order.order.duration,
      formatter: value => StringUtils.getFormattedDays(value),
    },
    {
      id: 'orderSum',
      label: t('order.orderSum'),
      value: getSum(order.order),
    },
    {
      id: 'orderDiscount',
      label: t('order.orderDiscount'),
      value: order.order.sum,
      formatter: value => order.order.type === 'DISCOUNT_PERCENT' ? `${value} %` : order.order.type === 'DISCOUNT_AMOUNT' ? StringUtils.getFormattedSum(value) : "",
    },
    {
      id: 'orderVatAmount',
      label: t('order.orderVatAmount'),
      value: order.order.vatAmount,
      formatter: value => StringUtils.getFormattedSum(value),
    },
    {
      id: 'orderCreated',
      label: t('order.orderCreated'),
      value: order.order.creationDate,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'orderUpdated',
      label: t('order.orderUpdated'),
      value: order.order.updateDate,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'orderEffectiveDate',
      label: t('order.orderEffectiveDate'),
      value: order.order.effectiveDate,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'orderExecutedAt',
      label: t('order.orderExecutedAt'),
      value: order.order.executedAt,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'orderVoucher',
      label: t('order.orderVoucher'),
      value: order.order.voucher,
    },
    {
      id: 'orderPromotion',
      label: t('order.orderPromotion'),
      value: order.order.promotion,
    },
    {
      id: 'orderFreePeriod',
      label: t('order.orderFreePeriod'),
      value: order.order.freePeriod,
      formatter: value => StringUtils.getFormattedDays(value),
    },
    {
      id: 'orderChannels',
      label: t('order.orderChannels'),
      value: order.order.orderLines,
      formatter: formatOrderLines,
    },
    ...(order.order.type === 'CAPTURE'
      ? [
          {
            id: 'orderRetryCount',
            label: t('order.orderRetryCount'),
            value: order.order.retryCount,
          },
          {
            id: 'orderLastGrace',
            label: t('order.orderLastGrace'),
            value: order.order.lastGrace,
            formatter: value => (value ? 'Ja' : 'Nej'),
          },
        ]
      : []),
    ...(order.order.formerProductId
      ? [
          {
            id: 'orderFormerProductId',
            label: t('order.orderFormerProductId'),
            value: order.order.formerProductId,
          },
          {
            id: 'orderFormerProductName',
            label: t('order.orderFormerProductName'),
            value: order.order.formerProductId,
            formatter: formatProductName,
          },
        ]
      : []),
    ...(order.order.provider !== 1
      ? [
          {
            id: 'orderVasToken',
            label: t('order.orderVasToken'),
            value: order.order.vastoken,
          },
          {
            id: 'orderPartnerAssociateId',
            label: t('order.orderPartnerAssociateId'),
            value: order.order.partnerAssociateId,
          },
          {
            id: 'orderPartnerAgreementId',
            label: t('order.orderPartnerAgreementId'),
            value: order.order.partnerAgreementId,
          },
        ]
      : []),
  ];

  const getOrderPaymentInfo = () => {
    if (order.paymentInfo) {
      return [
        {
          id: 'paymentCardType',
          label: t('payment.paymentCardType'),
          value: order.paymentInfo.cardType,
        },
        {
          id: 'paymentCardCountry',
          label: t('payment.paymentCardCountry'),
          value: order.paymentInfo.cardCountry,
        },
        {
          id: 'paymentCardExpiration',
          label: t('payment.paymentCardExpiration'),
          value: order.paymentInfo,
          formatter: value => `${value.cardExpMonth}/${value.cardExpYear}`,
        },
        {
          id: 'paymentCardNumber',
          label: t('payment.paymentCardNumber'),
          value: order.paymentInfo,
          formatter: value =>
            value.cardPrefix
              ? `${value.cardPrefix}${value.cardMask.slice(value.cardPrefix.length)}`
              : value.cardMask,
        },
        {
          id: 'paymentCardId',
          label: t('payment.paymentCardId'),
          value: order.paymentInfo.pic,
        },
        {
          id: 'paymentCardUpdate',
          label: t('payment.paymentCardUpdate'),
          value: order.paymentInfo.cardUpdate,
          formatter: value => DateUtils.getDateTime(value),
        },
      ];
    }
    if (order.order.paymentInfo && order.order.paymentInfo.paymentOwner === 'DIBS') {
      return [
        {
          id: 'paymentPsp',
          label: t('payment.paymentPsp'),
          value: order.order.paymentInfo.paymentOwner,
        },
        {
          id: 'paymentType',
          label: t('payment.paymentType'),
          value: order.order.paymentInfo.paymentType,
        },
        {
          id: 'paymentCardCountry',
          label: t('payment.paymentCardCountry'),
          value: order.order.paymentInfo.cardCountry,
        },
        {
          id: 'paymentCardExpiration',
          label: t('payment.paymentCardExpiration'),
          value: order.order.paymentInfo,
          formatter: value => `${value.expireMonth}/${value.expireYear}`,
        },
        {
          id: 'paymentCardNumber',
          label: t('payment.paymentCardNumber'),
          value: order.order.paymentInfo.cardNo,
        },
        {
          id: 'paymentCardId',
          label: t('payment.paymentCardId'),
          value: order.order.paymentInfo.pic,
        },
        {
          id: 'paymentCardUpdate',
          label: t('payment.paymentCardUpdate'),
          value: order.order.paymentInfo.cardUpdate,
          formatter: value => DateUtils.getDateTime(value),
        },
      ];
    }
    return [];
  };

  const getOrderTransactions = () =>
    _.defaultTo(order.transactions, []).map((transaction, index) => ({
      id: transaction.id,
      title: DateUtils.getDateTime(transaction.createdDate),
      subtitle: transaction.type,
      content: (
        <Box className={classes.transactionContent}>
          <Box>
            <Typography variant="caption" className={classes.transactionLabel}>
              {`${t('transaction.transactionId')}:`}
            </Typography>
            <Typography
              id={StringUtils.makeId('OrderTransactions', `item-${index}-transactionId`)}
              variant="caption"
            >
              {transaction.id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" className={classes.transactionLabel}>
              {`${t('transaction.transactionPspId')}:`}
            </Typography>
            <Typography
              id={StringUtils.makeId('OrderTransactions', `item-${index}-transactionPspId`)}
              variant="caption"
            >
              {transaction.pspTransactionId}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" className={classes.transactionLabel}>
              {`${t('transaction.transactionAmount')}:`}
            </Typography>
            <Typography
              id={StringUtils.makeId('OrderTransactions', `item-${index}-transactionAmount`)}
              variant="caption"
            >
              {StringUtils.getFormattedSum(transaction.amount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" className={classes.transactionLabel}>
              {`${t('transaction.transactionVatAmount')}:`}
            </Typography>
            <Typography
              id={StringUtils.makeId('OrderTransactions', `item-${index}-transactionVatAmount`)}
              variant="caption"
            >
              {StringUtils.getFormattedSum(transaction.vatAmount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" className={classes.transactionLabel}>
              {`${t('transaction.transactionDescription')}:`}
            </Typography>
            <Typography
              id={StringUtils.makeId('OrderTransactions', `item-${index}-transactionDescription`)}
              variant="caption"
            >
              {transaction.description ? transaction.description : ''}
            </Typography>
          </Box>
        </Box>
      ),
    }));

  const getOrderEvents = () =>
    _.defaultTo(order.events, []).map(event => ({
      id: event.created,
      title: DateUtils.getDateTime(event.created),
      subtitle: event.creator,
      content: <Typography variant="body2">{formatEvent(event)}</Typography>,
    }));

  const handleShowRefundOrderDialog = () => {
    showDialog({
      title: t('order.orderRefund'),
      content: params => (
        <OrderRefund order={order} onChange={params.setValue} onError={params.setDisabled} />
      ),
      onConfirm: onRefundOrder,
      confirmButtonText: t('common.refund'),
    });
  };

  const handleShowCancelOrderDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('order.orderCancelConfirm')}</Typography>,
      onConfirm: onCancelOrder,
    });
  };

  const renderOrderActions = () => {
    const allowRefundOrder =
      order.order.type === 'CAPTURE' &&
      order.order.status === 'COMPLETED' &&
      order.refund?.refundable;
    const allowCancelOrder =
      order.order.type !== 'CAPTURE' && ['PENDING', 'PLACED'].includes(order.order.status);
    return (
      <>
        {allowRefundOrder && (
          <Button
            id="OrderDetails-refund"
            color="primary"
            variant="outlined"
            startIcon={<Icon>sync</Icon>}
            onClick={handleShowRefundOrderDialog}
            disabled={actionsDisabled}
          >
            {t('order.orderRefund')}
          </Button>
        )}
        {allowCancelOrder && (
          <Button
            id="OrderDetails-cancel"
            color="primary"
            variant="outlined"
            startIcon={<Icon>cancel_presentation</Icon>}
            onClick={handleShowCancelOrderDialog}
            disabled={actionsDisabled}
          >
            {t('order.orderCancel')}
          </Button>
        )}
      </>
    );
  };

  return (
    <Box id="OrderDetails-container" className={classes.container}>
      <Box className={classes.actions}>
        <Button
          id="OrderDetails-back"
          className={classes.back}
          startIcon={<Icon>chevron_left</Icon>}
          onClick={onHideDetails}
          disabled={actionsDisabled}
        >
          {t('common.back')}
        </Button>
        {renderOrderActions()}
      </Box>
      <Box className={classes.content}>
        <Box className={classes.wrapper}>
          <DetailsSection
            id="OrderDetails"
            title={t('order.orderDetails')}
            className={classes.section}
            data={getOrderDetails()}
          />
        </Box>
        <Box className={classes.wrapper}>
          <DetailsSection
            id="OrderPaymentInfo"
            title={t('order.orderPaymentInfo')}
            className={classes.section}
            data={getOrderPaymentInfo()}
            emptyContent={<EmptyState message={t('order.orderPaymentEmpty')} center={false} />}
          />
          {order.transactions && (
            <ListSection
              id="OrderTransactions"
              title={t('order.orderTransactions')}
              className={clsx(classes.section, classes.transactionsSection)}
              data={getOrderTransactions()}
              emptyContent={
                <EmptyState message={t('order.orderTransactionsEmpty')} center={false} />
              }
            />
          )}
        </Box>
        <Box className={classes.wrapper}>
          <ListSection
            id="OrderEvents"
            title={t('order.orderEvents')}
            className={clsx(classes.section, classes.eventsSection)}
            data={getOrderEvents()}
            emptyContent={<EmptyState message={t('order.orderEventsEmpty')} center={false} />}
          />
        </Box>
      </Box>
    </Box>
  );
}

OrderDetails.propTypes = {
  order: PropTypes.shape({
    order: PropTypes.shape({
      creationDate: PropTypes.string,
      duration: PropTypes.number,
      effectiveDate: PropTypes.string,
      executedAt: PropTypes.string,
      formerProductId: PropTypes.number,
      freePeriod: PropTypes.number,
      lastGrace: PropTypes.bool,
      orderId: PropTypes.number,
      orderLines: PropTypes.arrayOf(PropTypes.object),
      orderStatus: PropTypes.string,
      pendingOrderStatus: PropTypes.string,
      partnerAgreementId: PropTypes.number,
      partnerAssociateId: PropTypes.number,
      paymentInfo: PropTypes.shape({
        paymentOwner: PropTypes.string,
        paymentType: PropTypes.string,
        cardNo: PropTypes.string,
        cardCountry: PropTypes.string,
        cardUpdate: PropTypes.string,
        expireMonth: PropTypes.number,
        expireYear: PropTypes.number,
        pic: PropTypes.string,
        recurringId: PropTypes.string,
      }),
      product: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        price: PropTypes.number,
        discountDays: PropTypes.number,
        durationDays: PropTypes.number,
        commitmentDays: PropTypes.number,
        productGroup: PropTypes.string,
      }),
      provider: PropTypes.number,
      retryCount: PropTypes.number,
      status: PropTypes.string,
      sum: PropTypes.number,
      vatAmount: PropTypes.number,
      type: PropTypes.string,
      updateDate: PropTypes.string,
      userId: PropTypes.string,
      vastoken: PropTypes.string,
      voucher: PropTypes.string,
    }),
    paymentInfo: PropTypes.shape({
      recurringId: PropTypes.string,
      paymentType: PropTypes.string,
      cardUpdate: PropTypes.string,
      cardType: PropTypes.string,
      cardCountry: PropTypes.string,
      cardExpMonth: PropTypes.number,
      cardExpYear: PropTypes.number,
      cardMask: PropTypes.string,
      cardPrefix: PropTypes.string,
      pic: PropTypes.string,
    }),
    refund: PropTypes.shape({
      refundable: PropTypes.bool,
      refundedAmount: PropTypes.number,
    }),
    transactions: PropTypes.arrayOf(PropTypes.object),
    events: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRefundOrder: PropTypes.func.isRequired,
  onCancelOrder: PropTypes.func.isRequired,
  onHideDetails: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  getSum: PropTypes.func.isRequired,
};

export default OrderDetails;
